import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ChevronDown, Menu, X, Shirt, Coffee, Printer, Gift, Package, BookOpen, PenLine, HelpCircle, Mail, Sparkles, Search, Heart, Palette, Megaphone, Users, Home as HomeIcon, CreditCard } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import SearchBox from "./SearchBox";

const MORE_LINKS = [
  { label: "Polo T-Shirts", to: "/polos", icon: Shirt, section: "Shop" },
  { label: "Regular T-Shirts", to: "/t-shirts/collection", icon: Shirt, section: "Shop" },
  { label: "Business Cards", to: "/business-printing#business-cards", icon: CreditCard, section: "Shop" },
  { label: "Group Orders", to: "/group-orders", icon: Users, section: "Shop" },
  { label: "Custom Design", to: "/custom-design", icon: Palette, section: "Shop" },
  { label: "Hoodies & Sweatshirts", to: "/hoodies-sweatshirts", icon: Shirt, section: "Shop" },
  { label: "Cups & Drinkware", to: "/cups", icon: Coffee, section: "Shop" },
  { label: "Personalized Gifts", to: "/gifts", icon: Gift, section: "Shop" },
  { label: "Custom Boxes", to: "/custom-boxes", icon: Package, section: "Shop" },
  { label: "Our Story", to: "/about", icon: BookOpen, section: "Company" },
  { label: "Blog", to: "/blog", icon: PenLine, section: "Company" },
  { label: "How It Works", to: "/how-it-works", icon: HelpCircle, section: "Company" },
  { label: "Contact", to: "/contact", icon: Mail, section: "Company" },
  { label: "Our Work", to: "/our-work", icon: Sparkles, section: "Company" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const closeTimer = React.useRef(null);

  const hoverAt = React.useRef(0);
  const openMore = () => { if (closeTimer.current) clearTimeout(closeTimer.current); if (!moreOpen) hoverAt.current = Date.now(); setMoreOpen(true); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setMoreOpen(false), 180); };

  const links = [
    { label: "Marketing & Websites", to: "/" },
    { label: "Grow Your Team", to: "/grow-your-team" },
    { label: "Business Printing", to: "/business-printing" },
    { label: "T-Shirt Printing", to: "/t-shirts", wide: true },
    { label: "Community", to: "/community", wide: true },
  ];
  const overflowLinks = [
    { label: "T-Shirt Printing", to: "/t-shirts", icon: Shirt },
    { label: "Community", to: "/community", icon: Users },
  ];

  const shopLinks = MORE_LINKS.filter((l) => l.section === "Shop");
  const companyLinks = MORE_LINKS.filter((l) => l.section === "Company");

  return (
    <div className="sticky top-0 z-50">
      <div className="bg-[color:var(--dms-peach-light)] text-[color:var(--dms-navy)] text-[13px] py-2 px-4 text-center">
        <span className="opacity-90">Have an idea but can't design it? We'll create it for you — </span>
        <Link to="/custom-design" className="underline underline-offset-4 font-medium hover:text-[color:var(--dms-navy-dark)]">start my custom design</Link>
      </div>

      <header className="bg-[color:var(--dms-navy)] shadow-sm">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#f5efe4] flex items-center justify-center overflow-hidden ring-1 ring-white/10">
              <span className="font-serif italic text-[color:var(--dms-navy)] text-[15px]">DMS</span>
            </div>
            <span className="font-serif italic text-white text-[22px]">Design My Story</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
            {links.map((l) => (
              <Link key={l.label} to={l.to} className={`nav-link ${l.wide ? "hidden xl:inline" : ""}`} data-testid={`nav-${l.to.replace(/[^a-z]/g, "") || "home"}`}>{l.label}</Link>
            ))}
            <div className="relative" onMouseEnter={openMore} onMouseLeave={scheduleClose}>
              <button className="nav-link inline-flex items-center gap-1" onClick={() => { if (Date.now() - hoverAt.current > 400) setMoreOpen((v) => !v); }} data-testid="nav-more-btn">
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-3 w-[640px] bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(20,36,61,0.35)] border border-[color:var(--dms-navy)]/10 p-6 grid grid-cols-2 gap-6 z-50" onMouseEnter={openMore} onMouseLeave={scheduleClose}>
                  <div>
                    <p className="kicker text-[10px] mb-3">Shop</p>
                    <ul className="space-y-1">
                      {shopLinks.map((l) => {
                        const Icon = l.icon;
                        return (
                          <li key={l.label}>
                            <Link to={l.to} onClick={() => setMoreOpen(false)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[color:var(--dms-cream-2)] group transition-colors">
                              <span className="w-9 h-9 rounded-full bg-[color:var(--dms-peach-light)]/50 group-hover:bg-[color:var(--dms-peach)] flex items-center justify-center transition-colors">
                                <Icon className="w-4 h-4 text-[color:var(--dms-navy)]" />
                              </span>
                              <span className="text-sm text-[color:var(--dms-navy)] font-medium">{l.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div>
                    <ul className="space-y-1 mb-4 xl:hidden" data-testid="nav-more-services">
                      {overflowLinks.map((l) => {
                        const Icon = l.icon;
                        return (
                          <li key={l.label}>
                            <Link to={l.to} onClick={() => setMoreOpen(false)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[color:var(--dms-cream-2)] group transition-colors">
                              <span className="w-9 h-9 rounded-full bg-[color:var(--dms-peach-light)]/50 group-hover:bg-[color:var(--dms-peach)] flex items-center justify-center transition-colors">
                                <Icon className="w-4 h-4 text-[color:var(--dms-navy)]" />
                              </span>
                              <span className="text-sm text-[color:var(--dms-navy)] font-medium">{l.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="kicker text-[10px] mb-3">Company</p>
                    <ul className="space-y-1">
                      {companyLinks.map((l) => {
                        const Icon = l.icon;
                        return (
                          <li key={l.label}>
                            <Link to={l.to} onClick={() => setMoreOpen(false)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[color:var(--dms-cream-2)] group transition-colors">
                              <span className="w-9 h-9 rounded-full bg-[color:var(--dms-peach-light)]/50 group-hover:bg-[color:var(--dms-peach)] flex items-center justify-center transition-colors">
                                <Icon className="w-4 h-4 text-[color:var(--dms-navy)]" />
                              </span>
                              <span className="text-sm text-[color:var(--dms-navy)] font-medium">{l.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                    <button onClick={() => { setMoreOpen(false); setCartOpen(true); }} className="mt-3 w-full flex items-center gap-3 p-2 rounded-lg bg-[color:var(--dms-navy)] text-white hover:bg-[color:var(--dms-navy-dark)] transition-colors">
                      <span className="w-9 h-9 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center">
                        <ShoppingBag className="w-4 h-4 text-[color:var(--dms-navy)]" />
                      </span>
                      <span className="text-sm font-semibold tracking-wider uppercase">Your Cart{count > 0 ? ` (${count})` : ""}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-4">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" className="text-white/90 hover:text-[color:var(--dms-peach)] transition-colors">
              <Search className="w-5 h-5" />
            </button>
            <Link to="/wishlist" aria-label="Wishlist" className="relative text-white/90 hover:text-[color:var(--dms-peach)] transition-colors">
              <Heart className="w-5 h-5" />
              {wishCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)] text-[10px] font-bold flex items-center justify-center">{wishCount}</span>
              )}
            </Link>
            <button onClick={() => setCartOpen(true)} aria-label="Cart" className="relative text-white/90 hover:text-[color:var(--dms-peach)] transition-colors">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)] text-[10px] font-bold flex items-center justify-center">{count}</span>
              )}
            </button>
            <Link to="/t-shirts" className="btn-peach hidden md:inline-flex">View more</Link>
            <button className="lg:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-[color:var(--dms-navy-dark)] px-5 py-4 max-h-[80vh] overflow-y-auto">
            <button onClick={() => { setOpen(false); setSearchOpen(true); }} className="w-full flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-white/85 text-sm mb-4"><Search className="w-4 h-4" /> Search products</button>
            <div className="space-y-2">
              {links.map((l) => (
                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block nav-link py-2">{l.label}</Link>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="kicker text-[10px] text-white/70">Shop</p>
              <div className="mt-2 space-y-1">
                {shopLinks.map((l) => (
                  <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block text-white/85 text-sm py-2 hover:text-[color:var(--dms-peach)]">{l.label}</Link>
                ))}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="kicker text-[10px] text-white/70">Company</p>
              <div className="mt-2 space-y-1">
                {companyLinks.map((l) => (
                  <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block text-white/85 text-sm py-2 hover:text-[color:var(--dms-peach)]">{l.label}</Link>
                ))}
                <button onClick={() => { setOpen(false); setCartOpen(true); }} className="block text-white/85 text-sm py-2 hover:text-[color:var(--dms-peach)]">Your Cart</button>
              </div>
            </div>
          </div>
        )}
      </header>
      {searchOpen && <SearchBox onClose={() => setSearchOpen(false)} />}
    </div>
  );
}
